/**
 * Merkliste der selbst gestarteten Duelle.
 *
 * Warum es das braucht: Ein Duell entsteht als Link, den der Herausforderer
 * verschickt. Danach war der Code nirgends mehr - er lebte nur noch in der
 * fremden Chat-App, in die er geteilt wurde. Wer sein Duell wiederfinden wollte,
 * musste den eigenen Nachrichtenverlauf durchsuchen. Und ob jemand geantwortet
 * hatte, erfuhr er ueberhaupt nicht.
 *
 * Diese Liste ist die Gegenmassnahme, und sie bleibt bewusst im Geraet: kein
 * Konto, keine Anmeldung, keine Zuordnung von Duellen zu einer Person auf dem
 * Server. Der Preis dafuer ist ehrlich zu nennen - wer den Browserspeicher
 * loescht oder das Geraet wechselt, verliert die Uebersicht. Die Duelle selbst
 * bleiben bestehen und ueber ihren Link erreichbar.
 */

import type { Discipline } from './disciplines'

const DUELS_KEY = 'som.duels.v1'

/** Mehr als das behaelt niemand im Blick; aeltere fallen hinten heraus. */
const MAX_DUELS = 20

export interface StoredDuel {
  code: string
  discipline: Discipline
  variant: string | null
  /** Das eigene Ergebnis, mit dem herausgefordert wurde. */
  distanceCm: number
  timeMs: number
  /** Zeitstempel in Millisekunden seit 1970. */
  createdAt: number
  /** Wie viele Antworten beim letzten Ansehen bekannt waren. */
  seenAttempts: number
}

export function loadDuels(): StoredDuel[] {
  try {
    const raw = localStorage.getItem(DUELS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as StoredDuel[]
    return Array.isArray(parsed) ? parsed.filter(isValid) : []
  } catch {
    return []
  }
}

export function rememberDuel(duel: Omit<StoredDuel, 'createdAt' | 'seenAttempts'>): void {
  const existing = loadDuels().filter((entry) => entry.code !== duel.code)
  const next = [{ ...duel, createdAt: Date.now(), seenAttempts: 0 }, ...existing]
  write(next.slice(0, MAX_DUELS))
}

/** Nach dem Ansehen: Der aktuelle Stand gilt als bekannt, das Abzeichen erlischt. */
export function markDuelSeen(code: string, attempts: number): void {
  const duels = loadDuels()
  const entry = duels.find((duel) => duel.code === code)
  if (!entry) return
  entry.seenAttempts = attempts
  write(duels)
}

export function forgetDuel(code: string): void {
  write(loadDuels().filter((duel) => duel.code !== code))
}

/** Ist dieses Duell von mir gestartet worden? */
export function isOwnDuel(code: string): boolean {
  return loadDuels().some((duel) => duel.code === code)
}

function isValid(duel: unknown): duel is StoredDuel {
  if (typeof duel !== 'object' || duel === null) return false
  const entry = duel as Record<string, unknown>
  return typeof entry.code === 'string' && typeof entry.discipline === 'string'
}

function write(duels: StoredDuel[]): void {
  try {
    localStorage.setItem(DUELS_KEY, JSON.stringify(duels))
  } catch {
    // Speicher gesperrt oder voll. Die Duelle bleiben ueber ihren Link
    // erreichbar, nur die Uebersicht fehlt dann.
  }
}
